import axios from '../utils/axios';

// Error handler helper
const handleError = (error, defaultMessage) => {
  const message = error.response?.data?.message || defaultMessage;
  console.error(defaultMessage, error);
  throw new Error(message);
};

// LanguageMaster API
export const fetchLanguageMasters = async () => {
  try {
    const res = await axios.get('/api/content/language-masters');
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch language masters');
  }
};

// Language API
export const fetchLanguages = async () => {
  try {
    const res = await axios.get('/api/content/languages');
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch languages');
  }
};

export const createLanguage = async (name, nativeLanguage, foreignLanguage) => {
  try {
    const res = await axios.post('/api/content/languages', { name, nativeLanguage, foreignLanguage });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create language');
  }
};

export const updateLanguage = async (id, data) => {
  try {
    const res = await axios.put(`/api/content/languages/${id}`, data);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update language');
  }
};

export const deleteLanguage = async (id) => {
  try {
    const res = await axios.delete(`/api/content/languages/${id}`);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to delete language');
  }
};

// School Year API
export const fetchSchoolYears = async () => {
  try {
    const res = await axios.get('/api/content/schoolyears');
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch school years');
  }
};

export const createSchoolYear = async (name, language) => {
  try {
    const res = await axios.post('/api/content/schoolyears', { name, language });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create school year');
  }
};

export const updateSchoolYear = async (id, name) => {
  try {
    const res = await axios.put(`/api/content/schoolyears/${id}`, { name });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update school year');
  }
};

export const deleteSchoolYear = async (id) => {
  try {
    const res = await axios.delete(`/api/content/schoolyears/${id}`);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to delete school year');
  }
};

// Lecture API
export const fetchLectures = async () => {
  try {
    const res = await axios.get('/api/content/lectures');
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch lectures');
  }
};

export const createLecture = async (name, schoolYear) => {
  try {
    const res = await axios.post('/api/content/lectures', { name, schoolYear });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create lecture');
  }
};

export const updateLecture = async (id, name) => {
  try {
    const res = await axios.put(`/api/content/lectures/${id}`, { name });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update lecture');
  }
};

export const deleteLecture = async (id) => {
  try {
    const res = await axios.delete(`/api/content/lectures/${id}`);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to delete lecture');
  }
};

// Word API
export const fetchWords = async (lectureId) => {
  try {
    const params = lectureId ? { lecture: lectureId } : {};
    const res = await axios.get('/api/content/words', { params });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch words');
  }
};

export const createWord = async (nativeWord, foreignWord, lecture) => {
  try {
    const res = await axios.post('/api/content/words', { nativeWord, foreignWord, lecture });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create word');
  }
};

export const updateWord = async (id, data) => {
  try {
    const res = await axios.put(`/api/content/words/${id}`, data);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update word');
  }
};

export const deleteWord = async (id) => {
  try {
    const res = await axios.delete(`/api/content/words/${id}`);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to delete word');
  }
};

export const updateWordStats = async (id, challenged, correct) => {
  try {
    const res = await axios.patch(`/api/content/words/${id}/stats`, { challenged, correct });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update word statistics');
  }
};

// PracticeSession API
export const fetchPracticeSessions = async () => {
  try {
    const res = await axios.get('/api/content/practice-sessions');
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch practice sessions');
  }
};

export const createPracticeSession = async (lectures) => {
  try {
    const res = await axios.post('/api/content/practice-sessions', { lectures });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to create practice session');
  }
};

export const updatePracticeSession = async (id, data) => {
  try {
    const res = await axios.put(`/api/content/practice-sessions/${id}`, data);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update practice session');
  }
};

export const deletePracticeSession = async (id) => {
  try {
    const res = await axios.delete(`/api/content/practice-sessions/${id}`);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to delete practice session');
  }
};

export const startPracticeSession = async (id, translationDirection, quizType) => {
  try {
    const res = await axios.post(`/api/content/practice-sessions/${id}/start`, {
      translationDirection,
      quizType
    });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to start practice session');
  }
};

export const updatePracticeProgress = async (id, coveredWords, firstAttemptCorrect, totalWords) => {
  try {
    const res = await axios.patch(`/api/content/practice-sessions/${id}/progress`, {
      coveredWords,
      firstAttemptCorrect,
      totalWords
    });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to update practice progress');
  }
};

export const completePracticeSession = async (id, coveredWords, firstAttemptCorrect, totalWords) => {
  try {
    const res = await axios.post(`/api/content/practice-sessions/${id}/complete`, {
      coveredWords,
      firstAttemptCorrect,
      totalWords
    });
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to complete practice session');
  }
};

export const restartPracticeSession = async (id) => {
  try {
    const res = await axios.post(`/api/content/practice-sessions/${id}/restart`);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to restart practice session');
  }
};

// Dashboard API
export const fetchDashboardStats = async (weeks = 12, languageId = 'all') => {
  try {
    const res = await axios.get(`/api/content/dashboard/stats?weeks=${weeks}&languageId=${languageId}`);
    return res.data;
  } catch (error) {
    handleError(error, 'Failed to fetch dashboard statistics');
  }
};
